import React, { useMemo } from 'react';

import { CellProps } from 'react-table';
import { useTranslation } from 'react-i18next';
import { VisuallyHidden, DataTableColumns } from '@tq/ui-components';

import { Consultee, ConsulteeTeam, ValueLabelResponse } from 'types';
import { EditableTableCell, TeamsTableCell, PermissionsTableCell } from 'components';
import {
  manageOrganisationUserTableEditingRowState,
  manageOrganisationUserTableNameInputState,
  manageOrganisationUserTableIsRegisteredState,
  manageOrganisationUserTablePermissionState,
  manageOrganisationUserTableTeamsState,
} from 'store';
import { UserRowEditButtons, IsRegisteredTableCell } from '../components';

export const useUserTableColumns = (
  teamOptions: ValueLabelResponse[] | undefined
): DataTableColumns<Consultee>[] => {
  const { t } = useTranslation('create_organisation');

  const teamsToValueLabelResponse = (teams: ConsulteeTeam[]): ValueLabelResponse[] => {
    return teams.map((team) => {
      return {
        value: team.teamId,
        label: team.teamName,
      };
    });
  };

  return useMemo(
    () => [
      {
        accessor: 'consulteeName',
        disableSortBy: false,
        Header: () => t('name', 'Name'),
        Cell: (props: CellProps<Consultee, unknown>) => (
          <EditableTableCell
            editingState={manageOrganisationUserTableEditingRowState}
            rowIndex={props.row.index}
            rowValue={props.row.original.consulteeName}
            valueState={manageOrganisationUserTableNameInputState}
            label={t('name_input_label', 'Name')}
            placeholder={t('name_input_placeholder', 'User name')}
            type='text'
          />
        ),
        width: 180,
      },
      {
        accessor: 'email',
        disableSortBy: true,
        Header: () => t('email', 'Email'),
        Cell: (props: CellProps<Consultee, unknown>) => <>{props.row.original.email}</>,
        width: 280,
      },
      {
        accessor: 'permissionName',
        disableSortBy: true,
        Header: () => t('permission', 'Permission'),
        Cell: (props: CellProps<Consultee, unknown>) => (
          <PermissionsTableCell
            editingState={manageOrganisationUserTableEditingRowState}
            permissionState={manageOrganisationUserTablePermissionState}
            rowIndex={props.row.index}
            rowValue={props.row.original.permissionName}
            label={t('permission_input_label', 'Permission')}
          />
        ),
        width: 200,
      },
      {
        accessor: 'teams',
        disableSortBy: true,
        Header: () => t('teams', 'Team(s)'),
        Cell: (props: CellProps<Consultee, unknown>) => (
          <TeamsTableCell
            editingState={manageOrganisationUserTableEditingRowState}
            teamState={manageOrganisationUserTableTeamsState}
            options={teamOptions ?? []}
            rowIndex={props.row.index}
            rowValue={teamsToValueLabelResponse(props?.row.original.teams)}
            label={t('teams_input_label', 'Team(s)')}
          />
        ),
        width: 200,
      },
      {
        accessor: 'isRegistered',
        disableSortBy: true,
        Header: () => t('is_registered', 'Registered'),
        Cell: (props: CellProps<Consultee, unknown>) => (
          <IsRegisteredTableCell
            editingState={manageOrganisationUserTableEditingRowState}
            isRegisteredState={manageOrganisationUserTableIsRegisteredState}
            isRegisteredRowValue={props.row.original.isRegistered}
            rowIndex={props.row.index}
            rowValue={props.row.original.isRegistered ? 'Yes' : 'No'}
            label={''}
          />
        ),
        width: 130,
      },
      {
        accessor: 'id',
        disableSortBy: true,
        Cell: (props: CellProps<Consultee, unknown>) => (
          <UserRowEditButtons
            {...props}
            width={220}
            editingState={manageOrganisationUserTableEditingRowState}
          />
        ),
        Header: () => <VisuallyHidden>{t('manage_header', 'Manage')}</VisuallyHidden>,
        width: 220,
      },
    ],
    [teamOptions, t]
  );
};
